import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { CopilotComposer } from "../components/CopilotComposer";
import { CopilotComposerChips } from "../components/CopilotComposerChips";
import { DynamicOutput } from "../components/DynamicOutput";
import { GravityCanvas } from "../components/GravityCanvas";
import { CopilotHeroAsset } from "../components/CopilotHeroAsset";
import { HomeProofStrip } from "../components/HomeProofStrip";
import { HomeServices } from "../components/HomeServices";
import { HomeFaq, HOME_FAQ } from "../components/HomeFaq";
import { SERVICES } from "../content/services";
import { business } from "../content/business";

const HOME_TITLE = "NEXIRT — Custom Software & AI Agent Workflow Integration";
const HOME_DESCRIPTION =
  "NEXIRT is a Las Vegas custom software studio. We build portals, dashboards, and business systems, and integrate AI agents into your workflows. Powered by AI, run by humans.";

// Owner directive 2026-04-25 retired the hero A/B test (rev 11);
// owner directive 2026-04-27 swapped the default from v1 → v4
// (rev 14). v4 paper-graph + cursor-tracked violet flashlight is
// the default for ALL users; v1 wormhole stays accessible via
// `?hero=v1` for testing / preview (rev-13 brightening preserved).

// Rev 25 Phase 2 Loop I — BENTO / PROOF / PROCESS / TIERS arrays retired.
// Their content moved into the DynamicOutput panels under
// apps/web/src/components/panels/. Service list now lives in MegaMenu.

export default function Home() {
  return (
    <>
      <SEO
        title={HOME_TITLE}
        description={HOME_DESCRIPTION}
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": "https://nexirt.com/#website",
            url: "https://nexirt.com/",
            name: "NEXIRT",
            description: HOME_DESCRIPTION,
            inLanguage: "en-US",
            publisher: { "@id": "https://nexirt.com/#organization" },
          },
          {
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "@id": "https://nexirt.com/#studio",
            name: business.name,
            url: "https://nexirt.com/",
            image: "https://nexirt.com/og-image-v2.png",
            logo: "https://nexirt.com/logo-apex-primary.svg",
            description: HOME_DESCRIPTION,
            slogan: "Powered by AI. Run by humans.",
            email: business.contact.email,
            priceRange: "$$$",
            address: {
              "@type": "PostalAddress",
              streetAddress: business.address.street,
              addressLocality: business.address.city,
              addressRegion: business.address.region,
              postalCode: business.address.postalCode,
              addressCountry: business.address.country,
            },
            areaServed: { "@type": "Country", name: "United States" },
            openingHoursSpecification: {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              opens: "09:00",
              closes: "18:00",
            },
            knowsAbout: [
              "Custom software development",
              "AI agents",
              "AI workflow automation",
              "Large language model integration",
              "Claude",
              "AWS serverless architecture",
              "Web portals",
              "Business dashboards",
            ],
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "NEXIRT services",
              itemListElement: SERVICES.map((svc) => ({
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: svc.title,
                  description: svc.line,
                  url: `https://nexirt.com/services/${svc.slug}`,
                },
              })),
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: HOME_FAQ.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]}
      />

      {/* HERO — Rev 25.5 three-area grid (was Rev 25.1 two-column shell).
          GravityCanvas full-bleed behind. Grid areas:
            Desktop ≥1024px: 2-col, areas "text robot" / "text chips" —
              composer in left column spans both rows, robot top-right,
              chips render BELOW robot in the right column.
            Mobile <1024px: single column, robot hidden (display:none),
              chips fall under the composer as a horizontal-scroll rail.

          Loop A of 25.5 uses EXPLICIT grid-template-areas rather than
          Tailwind lg:* conditional renders that silently failed in
          Rev 25.4 (L-032). Chip render moved to <CopilotComposerChips />
          sibling component; composer subscribes to a window event so
          UX stays the same wherever chips mount. */}
      <section
        className="relative overflow-hidden border-b border-nx-line/60"
        aria-labelledby="copilot-hero-h"
        data-rev25-1-hero
      >
        <GravityCanvas />
        <div className="nx-container relative z-[1] py-14 md:py-20 lg:py-24">
          <div className="nx-hero-grid" data-hero-grid>
            <div data-copilot-hero className="nx-hero-text max-w-2xl">
              {/* Eyebrow — pulsing green dot + mixed-case mono label per
                  design Home.html line 538. Copy refreshed from the stale
                  "sole engineer · v24" build tag to name what we sell. */}
              <span
                data-hero-eyebrow
                className="nx-hero-eyebrow inline-flex items-center gap-2 rounded-nx-full border border-nx-line bg-nx-surface/55 px-3 py-1 font-mono text-[11px] tracking-[0.04em] text-nx-text-dim"
              >
                <span className="nx-hero-eyebrow-dot" aria-hidden />
                Software studio · AI agents · accepting projects
              </span>
              <style>{`
                @keyframes nx-hero-eyebrow-pulse {
                  0%, 100% { opacity: 1; }
                  50%      { opacity: 0.45; }
                }
                .nx-hero-eyebrow-dot {
                  display: inline-block;
                  width: 6px;
                  height: 6px;
                  border-radius: 9999px;
                  background: var(--nx-success);
                  box-shadow: 0 0 10px var(--nx-success);
                  animation: nx-hero-eyebrow-pulse 2.4s ease-in-out infinite;
                }
                @media (prefers-reduced-motion: reduce) {
                  .nx-hero-eyebrow-dot { animation: none !important; }
                }
              `}</style>
              {/* Headline — Rev 26.2 swap to match the Rev 26.1 brand
                  tagline + OG image. Was the Rev 25.2 design-verbatim
                  "Describe what you need. We build it." (silver h1 +
                  violet em); now aligns with "Powered by AI. Run by
                  humans." Same CSS treatment: silver gradient on h1,
                  violet gradient on em. */}
              <h1
                id="copilot-hero-h"
                className="nx-hero-headline mt-5 font-display font-semibold tracking-tight text-[clamp(2.4rem,5.4vw,4.25rem)] leading-[1.02]"
              >
                Powered by AI.<br />
                <em>Run by humans.</em>
              </h1>
              <style>{`
                .nx-hero-headline {
                  background: linear-gradient(180deg,#fff 0%, #c7c9e0 70%, #8b8fb5 100%);
                  -webkit-background-clip: text;
                  background-clip: text;
                  color: transparent;
                  text-wrap: balance;
                }
                .nx-hero-headline em {
                  font-style: normal;
                  font-weight: 600;
                  background: linear-gradient(135deg,#E7E9F2 0%, #9EA3F5 50%, #6B4FE6 100%);
                  -webkit-background-clip: text;
                  background-clip: text;
                  color: transparent;
                }
              `}</style>
              {/* Sub-copy — Rev 25.4 reframed last clause from "with Newton"
                  to "with one of our engineers" per owner directive. */}
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-nx-text-dim md:text-base">
                A custom software and AI agent studio that ships systems, not screens.
                Tell the assistant what isn't working — it answers in
                components, code, and a calendar slot with one of our engineers.
              </p>
              <div className="mt-6">
                <CopilotComposer />
              </div>
            </div>
            {/* Hero asset — Spline robot + HUD. Hard-cropped to 480px
                (Loop B). Hidden below lg via the grid CSS. */}
            <div className="nx-hero-robot" data-hero-robot>
              <CopilotHeroAsset />
            </div>
            {/* Chip grid — Loop A. Renders below the robot in the right
                column at desktop; falls under the composer (single
                column) at mobile because the grid collapses + the robot
                area is display:none. layout="grid" works at both sizes;
                the parent grid handles the placement. */}
            <div className="nx-hero-chips" data-hero-chips>
              <CopilotComposerChips layout="grid" />
            </div>
          </div>
        </div>
      </section>

      {/* PROOF STRIP — Rev 25.6 Loop F. Live stats from /v1/public/stats
          with CountUp animation on IntersectionObserver entry. */}
      <HomeProofStrip />

      {/* WHAT WE BUILD — service catalog as plain linked HTML, so the
          offer is legible without prompting the assistant. */}
      <HomeServices />

      {/* DYNAMIC OUTPUT — Rev 25 Phase 2 Loop I. Replaces the previous
          PROOF / BENTO / PROCESS / PRICING TEASER stack (all sections'
          content is preserved across the 5 panels: KpiPanel / PricingPanel
          / ProcessPanel / CalendarPanel / ProofPanel). Phase 4 wires
          Anthropic tool-use so the composer above can drive panel
          selection from the live conversation; Phase 2 ships static
          mocks + the visual contract. */}
      <DynamicOutput />

      {/* FAQ — mirrors the FAQPage JSON-LD above. */}
      <HomeFaq />

      {/* FINAL CTA — Rev 25 Phase 2 Loop H rebuild per Home.html '03 Final
          CTA' treatment. Centered 1-col, max-w 760px, no card-on-card
          framing (the Dynamic Output canvas above carries the visual
          weight). Journal CTA hidden per owner default; substituted
          'See recent work' → /work. */}
      <section
        className="border-t border-nx-line/60"
        data-home-final-cta
        aria-labelledby="home-final-cta-h"
      >
        <div className="nx-container px-7 py-20 md:py-28">
          <div className="mx-auto max-w-[760px] text-center">
            <span className="inline-flex items-center gap-2 rounded-nx-full border border-nx-line bg-nx-surface/55 px-3 py-1 font-mono text-[10.5px] uppercase tracking-[0.18em] text-nx-text-faint">
              <span
                className="inline-block h-1.5 w-1.5 rounded-full bg-nx-accent-2"
                style={{ boxShadow: "0 0 8px var(--nx-accent-2)" }}
                aria-hidden
              />
              Ready when you are
            </span>
            <h2
              id="home-final-cta-h"
              className="mt-5 font-display font-semibold leading-[1.05] tracking-tight text-[clamp(2rem,4vw,3.2rem)] text-nx-text"
            >
              Tell us what isn't working.
            </h2>
            <p className="mx-auto mt-4 max-w-[520px] text-[16px] leading-snug text-nx-text-dim">
              Type into the assistant up top, or send a paragraph. One of
              our engineers answers personally within one business day.
            </p>
            <div className="mt-7 inline-flex flex-wrap justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-1.5 rounded-nx2 bg-nx-accent px-4 py-2.5 text-[14px] font-medium text-white shadow-[var(--nx-shadow-glow)] transition-[transform,filter] duration-150 ease-nx hover:-translate-y-[1px] hover:brightness-110"
                data-home-start-project
              >
                Start a project
              </Link>
              <Link
                to="/work"
                className="inline-flex items-center gap-1.5 rounded-nx2 border border-nx-line-strong bg-transparent px-4 py-2.5 text-[14px] text-nx-text transition-colors hover:bg-nx-surface-2"
                data-home-see-work
              >
                See recent work
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}


// Rev 25.1 — HeroSection retired. GravityCanvas + CopilotHeroAsset live
// inline in the hero section above. HeroV4Static + HeroV1Wormhole remain
// exported from @nexirt/ui for any other consumer; no longer imported here.
